import React, { useState, useCallback, useEffect } from 'react';
import {
    Box, Card, CardContent, Typography, TextField, MenuItem, Button,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, CircularProgress, Grid, Dialog, DialogTitle, DialogContent,
    DialogActions, IconButton, Tooltip, Avatar, InputAdornment,
} from '@mui/material';
import { Add, Refresh, ArrowBack, Search, Visibility, Delete, EmojiEvents, PictureAsPdf, Download } from '@mui/icons-material';
import { InvokeApi, apiList } from '../../services/ApiService';
import { Config } from '../../config/Config';
import ClassSectionGrid from '../../components/ClassSectionGrid';
import AsyncStorage from '../../services/AsyncStorage';
import { useAuth } from '../../context/AuthContext';

export default function Circular() {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dialog, setDialog] = useState(false);
    const [form, setForm] = useState({
        circular_name: '', circular_detail: ''
    });
    const [pdfFile, setPdfFile] = useState(null);
    const [viewItem, setViewItem] = useState(null);
    const { user, logout } = useAuth();
    const isTeacher = user?.role === 'Teacher';
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const data = await InvokeApi(Config.main_url + apiList.getCirculars, {
        });
        setRecords(data?.circulars || []);
        setLoading(false);
    }, []);

    useEffect(() => {
        load();
    }, []);

    const handleSave = async () => {
        if (!form.circular_name || !form.circular_detail) {
            alert('Circular name and detail are required'); return;
        }
        setSaving(true);
        const data = await InvokeApi(Config.main_url + apiList.addCircular, {
            title: form.circular_name,
            detail: form.circular_detail,
        });

        if (data?.code === '200') {
            setDialog(false);
            setPdfFile(null);
            load();
        } else alert(data?.message || 'Failed');
        setSaving(false);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this circular?')) return;
        await InvokeApi(Config.main_url + apiList.deleteCircular, { id, status: 'Deleted' });
        load();
    };

    const openCircular = async (ID) => {
        const token = await AsyncStorage.getItem('token');
        const url = `${Config.main_url}/generateCircular/${ID}`;
        try {
            const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) { alert('Failed to generate circular'); return; }
            const blob = await res.blob();
            window.open(URL.createObjectURL(blob), '_blank');
        } catch (e) { alert('Error: ' + e.message); }
    };

    const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h5" fontWeight={800} color="#1a237e">Circular</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {records.length} circulars
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Refresh">
                        <IconButton onClick={() => load()} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}>
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                    <Button variant="contained" startIcon={<Add />}
                        onClick={() => { setForm({ circular_name: '', circular_detail: '' }); setPdfFile(null); setDialog(true); }}
                        sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
                        Add Circular
                    </Button>
                </Box>
            </Box>

            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress sx={{ color: '#1a237e' }} /></Box>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ background: '#f5f6fa' }}>
                                    {['#', 'Circular', 'Files', isTeacher ? '' : 'Actions'].map(h => (
                                        <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13 }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {records.length === 0 ? (
                                    <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                                        No circulars
                                    </TableCell></TableRow>
                                ) : records.map((r, i) => (
                                    <TableRow key={r.ID || i} hover>
                                        <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{i + 1}</TableCell>
                                        <TableCell>
                                            <Typography fontWeight={600} fontSize={13}>{r.title}</Typography>
                                            <Typography fontSize={11} color="text.secondary" noWrap sx={{ maxWidth: 220 }}>{r.detail}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title="Generate Circular PDF">
                                                <Button size="small" variant="contained" startIcon={<PictureAsPdf />}
                                                    onClick={() => openCircular(r.ID)}
                                                    sx={{ background: '#e53935', borderRadius: 2, fontWeight: 700, fontSize: 10, '&:hover': { background: '#b71c1c' } }}>
                                                    Circular
                                                </Button>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            {isTeacher ? "" : <Tooltip title="Delete">
                                                <IconButton size="small" onClick={() => handleDelete(r.ID)} sx={{ color: '#c62828' }}>
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </Tooltip>}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Card>

            <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0' }}>
                    Add Circular
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Circular Name *" value={form.circular_name} onChange={f('circular_name')} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Circular Details" value={form.circular_detail} onChange={f('circular_detail')} multiline rows={2} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2.5, gap: 1, borderTop: '1px solid #f0f0f0' }}>
                    <Button onClick={() => { setDialog(false); setPdfFile(null); }} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" disabled={saving}
                        sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
                        {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    )
}